import { v4 as uuid } from 'uuid';
import { Server, Req } from 'one-server';

export class HAServer implements Server {
  private key: string;
  private monitor: boolean;
  private frequency: number;
  private apitimeout:number;
  private callback: (url: string, error: any)=>void;

  private servers: {
    id: string,
    base: string,
    health: string,
    active: boolean,
    monitored: false,
  }[];

  constructor(
    servers: any,
    callback: (url: string, error: any)=>void,
      
  ) {
    const {
      monitor = true,
      frequency = 10000,
      locations = [],
      apitimeout
    } = servers;
    this.key = uuid();
    this.monitor = monitor;
    this.frequency = frequency;
    this.apitimeout = apitimeout;
    this.callback= callback;
    this.servers = locations.map((location: any) => ({
      ...location,
      id: uuid(),
      active: true,
      monitored: false,
    }));

    console.debug(
      'one-server',
      'instance initialized',
      JSON.stringify({
        ...this,
      }),
    );
  }

  public forget(): Promise<any> {
    this.key = uuid();
    return;
  }

  /**
   * Helper to build a query string
   * (data is uri encoded)
   * @param data data to be converted to a query string
   */
  protected query = (data: any): string => {
    let path = '';
    if (data) {
      const entries: any = Object.entries(data);
      if ((entries) && (entries.length > 0)) {
        let sep = '?';
        for (const [name, value] of entries) {
          if (value) {
            const encoded = encodeURIComponent(value);
            path = `${path}${sep}${name}=${encoded}`;
            sep = '&';
          }
        }
      }
    }
    return path;
  }

  protected gatewayError = (status: number): boolean => {
    return (
      (status == 502) || (status == 503) || (status == 504)
    );
  }

  protected checkHealth = (
    server: any, frequency: number,
  ): void => {
    const { base, health } = server;
    const url = (health) || (`${base}healthcheck/`);
    console.debug(
      'one-server-health',
      'setting up monitor',
      JSON.stringify({
        url,
        server,
        frequency,
      }),
    );

    setTimeout(async () => {
      console.debug(
        'one-server-health',
        'time to check',
        JSON.stringify({
          url,
          server,
          frequency,
        }),
      );

      try {
        const { status } = await fetch(url);
        // gateway error
        if (this.gatewayError(status)) {
          console.debug(
            'one-server-health',
            'gateway error, mark inactive, re-start montior',
            JSON.stringify({
              url,
              status,
              server,
              frequency,
            }),
          );
          this.checkHealth(server, frequency);
        } else {
          // mark active and
          // stop monitoring
          server.active = true;
          server.monitored = false;
          console.debug(
            'one-server-health',
            'valid response, mark active, stop monitor',
            JSON.stringify({
              url,
              status,
              server,
              frequency,
            }),
          );
        }
      } catch (error) {
        this.callback(url, error);
        console.debug(
          'one-server-health',
          'not reachable, mark inactive, re-start montior',
          JSON.stringify({
            url,
            error,
            server,
            frequency,
          }),
        );
        this.checkHealth(server, frequency);
      }
    }, frequency);
  }

  protected update = (server: any, active: boolean): void => {
    server.active = active;
    const { monitor, frequency } = this;
    if ((monitor) && (!(active))) {
      const { monitored } = server;
      if (monitored) {
        console.debug(
          'one-server-health',
          'already monitored, ignore health check',
          JSON.stringify({
            monitor, frequency, server,
          }),
        );
      } else {
        console.debug(
          'one-server-health',
          'setting up health check monitor',
          JSON.stringify({
            monitor, frequency, server,
          }),
        );
        this.checkHealth(server, frequency);
        server.monitored = true;
      }
    }
  }
  protected fetchAbortController = (apitimeout: any) => {
    //Controller object that allows to abort Web Requests
    const fetchController = new AbortController();
    //Creates signal through fetchController
    const { signal } = fetchController;
    let abortController_values: any = [];
    // SetTimeout to abort request based on apitimeout parameter
    let timeouttrial = setTimeout(() => {
      fetchController.abort();
    }, apitimeout);

    abortController_values.push(signal);
    abortController_values.push(timeouttrial);

    return abortController_values;
  }

  protected handle(req: Req, method: string): Promise<any> {
    const key = this.key;
    const fetchController = new AbortController();
    return new Promise(async (resolve, reject) => {
      const list:any = [];
      const { servers, monitor,apitimeout } = this;
      const { signal } = fetchController;
      let timeouttrial = setTimeout(() => {
        fetchController.abort();
      }, apitimeout);
      // try active servers first
      // inactive servers will be made active
      // by the monitor thread
      for (const server of servers) {
        const { active } = server;
        if (active) {
          list.push(server);
        }
      }

      // no monitoring
      // try inactive servers as well
      if (!(monitor)) {
        for (const server of servers) {
          const { active } = server;
          if (!(active)) {
            list.push(server);
          }
        }
      }
      //Variable to  keep count of number of active servers in the list.
      let serverCount = 0;
      // flag request was handled
      let handled: boolean = false;

      // try the request on available servers
      for (const server of list) {
        // build the request
        const {
          path, data, headers, retry = true,
        } = req;
        const { id, base } = server;

        let url, body;
        if (method === 'GET') {
          url = `${base}${path}${this.query(data)}`;
        } else {
          url = `${base}${path}`;
          body = JSON.stringify(data);
        }

        console.debug(
          'one-server',
          'request url', JSON.stringify({
            id, url, method, data,
          }),
        );
        /**
         * We call fetchAbortController to create a new signal.
         * Signal is passed with the api request
         */
        let abortContorller_values: any = this.fetchAbortController(apitimeout);
        //Maintain the count value and increment after every server hit in case of multiple servers
        if(serverCount < list.length) {
          serverCount =   serverCount + 1 
        }
        try {
          const resp = await fetch(url, {
            method,
            body,
            headers,
            signal: abortContorller_values[0]
          }).then((resp) => {
            //If the  response is received within the timeout span we reset the timeout
            clearTimeout(abortContorller_values[1]);
            return resp;
            })
          .catch((err) => {
            /**
             * If the api request is Aborted/Cancelled.
             * We get the error name as AbortError in the response
             */
            if(err?.name === "AbortError") {
              /**
               * If the server is last active server in the list
               * We Reject the promise.
               * We check  the last active server basedon the serverCount we maintain
               * And paas the custom error object in the response
               */
              if(serverCount  == list.length) {
                reject({
                  "success": false,
                  "status": 408,
                  "results": [],
                  "errors": [
                    {
                      error_type: "ABORTED_ERROR",
                      display_text: "API Request Timed Out",
                      error_code: 408,
                      error_description: "API Request Timed Out",
                      error_detail: "API Request Timed Out",
                    }
                  ],
                })
              }
            }
            else {
              return err;
            }

          });

          const { status } = resp;
          const { key: currKey } = this;

          // make sure the transaction key
          // has changed in between
          if (key !== currKey) {
            handled = true;
            // reject response
            reject({
              status: -1,
            });

            console.debug(
              'one-server',
              'ignored, keys do not match',
              JSON.stringify({
                id, url, key, currKey,
              }),
            );

            if (this.gatewayError(status)) {
              console.debug(
                'one-server',
                'ignored, keys do not match, gateway error',
                JSON.stringify({
                  id, url, key, currKey,
                }),
              );
              // server not reachable
              this.update(server, false);
            } else {
              // just mark the server active
              this.update(server, true);
            }
          } else {
            console.debug(
              'one-server',
              'accepted, keys match',
              JSON.stringify({
                id, url, key, currKey,
              }),
            );

            if (this.gatewayError(status)) {
              console.debug(
                'one-server',
                'accepted, keys match, gateway error',
                JSON.stringify({
                  id, url, key, currKey,
                }),
              );
              // server not reachable
              this.update(server, false);
            } else {
              handled = true;
              this.update(server, true);

              // extract the response
              let datares:any;
              try {
                datares = await resp.json();
              } catch (exp) {
              }

              // resolve the promise
              if (resp.ok) {
                const results = (datares) && (datares.results);
                resolve(results);
              } else {
                const errors = (datares) && (datares.error);
                const detail = (datares) && (datares.detail)
                reject({
                  status, errors,detail,
                });

                console.debug(
                  'one-server',
                  'request error',
                  JSON.stringify({
                    id, key, url, status, data,
                  }),
                );
              }
            }
          }
        } catch (error) {
          // network/infrastructure error
          // mark the server inactive
          this.update(server, false);
          if (!(retry)) {
            // do not retry
            break;
          }
        }

        // break out of the loop
        if (handled) {
          // done
          break;
        }
      }

      if (!(handled)) {
        // request was not handled
        reject({
          status: 0,
          errors: [{
            error_description: 'Unable to connect, server(s) not available'
          }]
        });
      }
    });
  }

  public get(req: Req): Promise<any> {
    // make a GET request
    return this.handle(req, 'GET');
  }

  public post(req: Req): Promise<any> {
    // make a POST request
    return this.handle(req, 'POST');
  }

  public put(req: Req): Promise<any> {
    // make a POST request
    return this.handle(req, 'PUT');
  }
}
